import '../../styles/editcase/IdentityMismatchBanner.css';

const NOT_STATED = 'Not stated';

const STATE_COPY = {
    pending: 'These documents may belong to different patients.',
    approved: 'Remove the files that belong to another patient, then re-run extraction.',
    rejected: 'Confirmed as the same patient.',
};

function shown(value) {
    if (value === null || value === undefined || value === '') return NOT_STATED;
    return String(value);
}

/**
 * Banner for the synthesized "patient_identity_mismatch" red flag.
 *
 * The flag blocks the review gate while it is pending or approved. "Same
 * patient" dismisses it (status rejected) and opens the gate; "Different
 * patients" approves it and asks the user to remove the other patient's
 * files. Status writes go through the caller's onStatus, which reuses the
 * existing red flag review path (a repeat of the current status toggles
 * the flag back to pending, which is what Undo relies on).
 */
export default function IdentityMismatchBanner({ flag, onStatus, onGoToFiles }) {
    if (!flag) return null;

    const status = flag.status === 'approved' || flag.status === 'rejected' ? flag.status : 'pending';
    const documents = Array.isArray(flag.details?.documents) ? flag.details.documents : [];
    const distinct = flag.details?.distinct_patients;
    const role = status === 'rejected' ? 'status' : 'alert';

    return (
        <div className={`identity-banner identity-banner--${status}`} role={role}>
            <div className="identity-banner-header">
                <span className="identity-banner-title">{flag.flag || 'Patient identity mismatch'}</span>
                <span className="identity-banner-state">{STATE_COPY[status]}</span>
            </div>
            {flag.evidence && (
                <p className="identity-banner-evidence">{flag.evidence}</p>
            )}
            {documents.length > 0 && (
                <div className="identity-banner-table-wrap">
                    <table className="identity-banner-table">
                        <thead>
                            <tr>
                                <th scope="col">Document</th>
                                <th scope="col">Name</th>
                                <th scope="col">DOB</th>
                                <th scope="col">MRN</th>
                            </tr>
                        </thead>
                        <tbody>
                            {documents.map((d, i) => (
                                <tr key={`${d?.document ?? 'doc'}-${i}`}>
                                    <td>{shown(d?.document)}</td>
                                    <td>{shown(d?.name)}</td>
                                    <td>{shown(d?.dob)}</td>
                                    <td>{shown(d?.mrn)}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}
            {typeof distinct === 'number' && (
                <p className="identity-banner-count">
                    The model counted {distinct} distinct patient{distinct === 1 ? '' : 's'} across these documents.
                </p>
            )}
            <div className="identity-banner-actions">
                {status === 'pending' && (
                    <>
                        <button
                            type="button"
                            className="identity-banner-btn identity-banner-btn--primary"
                            onClick={() => onStatus?.('rejected')}
                        >
                            Same patient
                        </button>
                        <button
                            type="button"
                            className="identity-banner-btn identity-banner-btn--danger"
                            onClick={() => onStatus?.('approved')}
                        >
                            Different patients
                        </button>
                    </>
                )}
                {status === 'approved' && (
                    <>
                        <button
                            type="button"
                            className="identity-banner-btn identity-banner-btn--primary"
                            onClick={() => onGoToFiles?.()}
                        >
                            Go to files
                        </button>
                        <button
                            type="button"
                            className="identity-banner-btn identity-banner-btn--link"
                            onClick={() => onStatus?.('approved')}
                        >
                            Undo
                        </button>
                    </>
                )}
                {status === 'rejected' && (
                    <button
                        type="button"
                        className="identity-banner-btn identity-banner-btn--link"
                        onClick={() => onStatus?.('rejected')}
                    >
                        Undo
                    </button>
                )}
            </div>
        </div>
    );
}
